import { Suit, SUITS, CARD_POINTS, JOKER_POINTS, RANK_NAMES, SUIT_SYMBOLS } from './Constants';

export interface Card {
  suit: Suit;
  rank: number; // 1=Ace through 13=King, 0=Joker
  deckIndex: number; // 0 or 1
  id: string; // unique identifier
}

export function createCard(suit: Suit, rank: number, deckIndex: number): Card {
  return {
    suit,
    rank,
    deckIndex,
    id: `${suit}_${rank}_${deckIndex}`,
  };
}

export function createJoker(deckIndex: number, jokerIndex: number): Card {
  return {
    suit: 'spades', // arbitrary, not used
    rank: 0,
    deckIndex,
    id: `joker_${deckIndex}_${jokerIndex}`,
  };
}

export function isJoker(card: Card): boolean {
  return card.rank === 0;
}

export function isWild(card: Card): boolean {
  return card.rank === 0 || card.rank === 2;
}

export function isNaturalTwo(card: Card, meldSuit?: Suit, position?: number): boolean {
  // A 2 is natural if it's in a run of its own suit at position 2
  return card.rank === 2 && meldSuit !== undefined && card.suit === meldSuit && position === 1; // 0-indexed position 1 = rank 2
}

export function getCardPoints(card: Card): number {
  if (isJoker(card)) return JOKER_POINTS;
  return CARD_POINTS[card.rank] ?? 0;
}

export function getCardDisplayName(card: Card): string {
  if (isJoker(card)) return 'Joker';
  return `${RANK_NAMES[card.rank]}${SUIT_SYMBOLS[card.suit]}`;
}

export function createFullDeck(): Card[] {
  const cards: Card[] = [];
  for (let deckIndex = 0; deckIndex < 2; deckIndex++) {
    for (const suit of SUITS) {
      for (let rank = 1; rank <= 13; rank++) {
        cards.push(createCard(suit, rank, deckIndex));
      }
    }
    // 2 jokers per deck
    cards.push(createJoker(deckIndex, 0));
    cards.push(createJoker(deckIndex, 1));
  }
  return cards; // 108 cards
}

export function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function sortHand(cards: Card[]): Card[] {
  return [...cards].sort((a, b) => {
    if (isJoker(a) && !isJoker(b)) return -1;
    if (!isJoker(a) && isJoker(b)) return 1;
    if (isJoker(a) && isJoker(b)) return 0;
    // Sort by suit first, then by rank
    const suitOrder = SUITS.indexOf(a.suit) - SUITS.indexOf(b.suit);
    if (suitOrder !== 0) return suitOrder;
    return b.rank - a.rank;
  });
}
